import Joi from "joi";
import { useState, useEffect } from "react";
import SweetAlert from "react-bootstrap-sweetalert";
import { connect, useDispatch } from "react-redux";
import {
  getreportFrequencies,
  getReportNamesWithDescription,
  getUserPageBlocks,
  getUserTypes,
} from "../../../../../action/common/commonActionCreator";
import { updateReport } from "../../../../../action/masters/reportActionCreator";
import FloatingLabelDropDown from "../../../../../common-components/floatingdropdown";
import FloatingLabelTextInput from "../../../../../common-components/floatingtextinput";
import MultipleSelect from "../../../../../common-components/multipleSelect";
import {
  baseURL,
  cloneDeep,
  maxImageSize,
} from "../../../../../constants/commonConstants";
import { validateFields } from "../../../../../helper/joi-validation";
import {
  getAlertData,
  alertConstants,
} from "../../../../../helper/sweet-alert";
import { UserTypes } from "../../../../../models/dashboard/adminDashboard";
import {
  IEditReport,
  IReportEditData,
} from "../../../../../models/master/reports";
import { setReport } from "../../../../../store/slices/master/reportSlice";
import LoaderSpinner from "../../../../../common-components/loader";
import { masterType } from "../../../../../constants/masterTypes";
import { sortAlphabetically } from "../../../../../common-functions/commonFunctions";
import { setPageBlocks } from "../../../../../store/slices/common/commonSlice";
import { areObjectsEqual } from "../../../../../helper/generic-helpers";

const EditReport = (props: IEditReport) => {
  const dispatch = useDispatch();
  const {
    updateReport,
    setShowEdit,
    pageBlocks,
    getAllReports,
    report,
    getReportFrequencies,
    reportFrequencies,
    getReportNamesWithDescription,
    reportNamesWithDescription,
    getUserPageBlocks,
    getUserTypes,
    userTypes,
    userDetails,
  } = props;

  // states

  const [data, setData] = useState<IReportEditData>(cloneDeep(report || {}));
  const [initialData, setInitialData] = useState<IReportEditData | undefined>(
    undefined
  );
  const [errors, setErrors] = useState<any>(undefined);
  const [showAlert, setShowAlert] = useState<boolean>(false);
  const [alertType, setAlertType] = useState<any>("success");
  const [alertTitle, setAlertTitle] = useState<string>("Default");
  const [showAlertCancel, setShowAlertCancel] = useState<boolean>(false);
  const [imagePreview, setImagePreview] = useState<string | null>(null);
  const [imageName, setImageName] = useState<string | null>(null);
  // const ssoOptionsList = Object.entries(booleanOptions).map(([key, value]) => ({
  //   id: value,
  //   name: key,
  // }));
  // const [isSSOEnabled, setIsSSOEnabled] = useState<boolean>(false);
  const [isDisabled, setIsDisabled] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(false);
  const [hasChange, setHasChange] = useState(false);

  // change the data at the time onchange fields
  const onChange = (
    event:
      | React.ChangeEvent<HTMLInputElement>
      | React.ChangeEvent<HTMLSelectElement>
      | React.ChangeEvent<HTMLSelectElement>
      | React.ChangeEvent<HTMLSelectElement>
      | React.ChangeEvent<HTMLTextAreaElement>
  ) => {
    errors && delete errors[event.target.name];
    let _data = { ...data, [event.target.name]: event.target.value };
    if (event.target.name === "userType") {
      getUserPageBlocks(Number(event.target.value), masterType.report);
      _data.pageBlockIds = [];
      let _item = reportNamesWithDescription.find(
        (report) => report.id === event.target.value
      );
      _data.name = _item?.name;
      _data.description = _item?.name ? _item?.description : "";
      if (_data.description) errors && delete errors["description"];
    }
    setData(_data);
  };

  const onFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    errors && delete errors[event.target.name];
    if (event.target.files && event.target.files.length > 0) {
      const selectedFile = event.target.files[0];
      setImageName(selectedFile.name);
      const fileType = selectedFile.type;
      const fileSize = selectedFile.size;
      const validFileTypes = [
        "image/png",
        "image/jpeg",
        "image/jpg",
        "image/webp",
        "image/ico",
        "image/svg+xml",
      ];
      if (!validFileTypes.includes(fileType)) {
        setErrors({
          ...errors,
          file: "Invalid file type. Please upload an image.",
        });
      } else if (fileSize > maxImageSize) {
        setErrors({
          ...errors,
          file: "File size exceeds 1MB. Please upload a small file.",
        });
        setImagePreview(null);
        setImageName(null);
      } else {
        setData({ ...data, [event.target.name]: selectedFile });
        const fileURL = URL.createObjectURL(selectedFile);
        setImagePreview(fileURL);
      }
    }
  };

  useEffect(() => {
    if (report) {
      let _report = cloneDeep(report);
      setData(_report);
      setInitialData(_report);
      getUserPageBlocks(Number(_report.userType), masterType.report);
      if (_report.icon) {
        const imageUrl = baseURL + _report.icon;
        setImageName(_report.icon);
        setImagePreview(imageUrl);
      }
      // if (_report.sso) {
      //   setIsSSOEnabled(true);
      // }
    }
  }, [report]);

  // setting sweet alert dynamically
  const setAlert = (type: string) => {
    let alertData = getAlertData(type);
    if (alertData) {
      setShowAlert(true);
      setAlertType(alertData.type);
      setAlertTitle(alertData.title);
    }
  };

  // executed on clicking 'OK' in sweet alert.
  const onConfirm = () => {
    if (alertType === alertConstants.error) {
      setShowAlert(false);
    } else {
      getAllReports();
      setShowEdit(false);
    }
  };

  const urlRegex =
    /^https?:\/\/([a-z0-9]{2,}(-[a-z0-9]{2,})*\.)+[a-z0-9]{2,6}(\/[a-z0-9#?=&%\/\-_\.]*)?$/i;
  const schema = Joi.object().keys({
    name: Joi.string().required().label("Select Report Name"),
    userType: Joi.number().greater(0).required().label("Select User Type"),
    description: Joi.string().required().label("Description is required"),
    file: Joi.any().when("isFile", {
      is: true,
      then: Joi.any().optional(),
      otherwise: Joi.any().required().label("File is required"),
    }),
    url: Joi.string().required().regex(urlRegex).label("Enter Valid URL"),
    reportFrequency: Joi.number()
      .greater(0)
      .required()
      .label("Select Report Frequency"),
    pageBlockIds: Joi.array()
      .items(Joi.number().greater(0))
      .min(1)
      .required()
      .label("Select Page Block"),
    sso: Joi.string().when("isSSOEnabled", {
      is: true,
      then: Joi.string().required().regex(urlRegex).label("Enter Valid SSO"),
      otherwise: Joi.string().allow("", null).optional(),
    }),
  });

  const validationData = {
    ...data,
    isFile: Boolean(data.file),
    // isSSOEnabled: isSSOEnabled,
  };

  const onUpdate = async () => {
    const validationErrors = validateFields(validationData, schema);
    if (validationErrors === undefined) {
      const formData = new FormData();
      formData.append("id", data.id?.toString() || "");
      formData.append("userType", data.userType?.toString() || "");
      formData.append("name", data.name);
      formData.append("file", data.file);
      formData.append("url", data.url);
      formData.append("isActive", "true");
      formData.append("priority", data.priority.toString());
      formData.append(
        "description",
        data.description.replace(/\s+$/g, "").toString()
      );
      formData.append("reportFrequency", data.reportFrequency.toString());
      formData.append("collegeId", userDetails.currentCollege.id.toString());
      formData.append(
        "departmentId",
        userDetails.currentDepartment.id.toString()
      );
      // isSSOEnabled
      //   ? formData.append("sso", data.sso)
      //   : formData.append("sso", "");
      data.pageBlockIds.map((item, index) => {
        formData.append(`pageBlockIds[${index}]`, item.toString());
      });

      setLoading(true);
      setIsDisabled(true);
      try {
        await updateReport(formData, data.id ?? 0, setAlert);
      } finally {
        setLoading(false);
        setIsDisabled(false);
      }
    } else {
      setErrors(validationErrors);
    }
  };

  useEffect(() => {
    if (data && initialData) {
      setHasChange(!areObjectsEqual(data, initialData));
    }
  }, [data, initialData]);

  useEffect(() => {
    getReportFrequencies();
    getReportNamesWithDescription();
    getUserTypes();
  }, []);

  useEffect(() => {
    return () => {
      dispatch(setReport(undefined));
      dispatch(setPageBlocks([]));
    };
  }, []);

  return (
    <>
      {data && (
        <>
          <div className="modal-header">
            <h1 className="modal-title fs-5" id="addnewbuttonsLabel">
              Update Report
            </h1>
            <button
              type="button"
              className="btn-close"
              data-bs-dismiss="modal"
              aria-label="Close"
              onClick={() => setShowEdit(false)}
            ></button>
          </div>
          <div className="modal-body">
            <div className="row">
              <div className="col-md-12 form-group">
                <FloatingLabelDropDown
                  label={"User Type "}
                  onChange={onChange}
                  isMandatory={true}
                  value={data.userType}
                  name={"userType"}
                  optionsList={sortAlphabetically(
                    userTypes?.filter(
                      (userType) =>
                        userType.id !== UserTypes.Student &&
                        userType.id !== UserTypes.Freshman
                    )
                  )}
                />
                {errors?.userType && (
                  <div className="text-danger">{errors.userType}</div>
                )}
              </div>
              <div className="col-md-6 form-group">
                <FloatingLabelDropDown
                  label={"Report Name "}
                  onChange={onChange}
                  isMandatory={true}
                  value={data.name}
                  name={"name"}
                  optionsList={sortAlphabetically(reportNamesWithDescription)}
                />
                {errors?.name && (
                  <div className="text-danger">{errors.name}</div>
                )}
              </div>
              <div className="col-md-6 form-group">
                <label className="profile_details_text">
                  Icon<i className="label-asterisk"> * </i>
                </label>
                <div className="inputfile-flex">
                  {imagePreview && (
                    <div className="inputfile-img">
                      <img
                        src={imagePreview}
                        alt="Preview"
                        style={{ width: "50px", height: "50px" }}
                      />
                    </div>
                  )}
                  <div className="inputfile-wrap">
                    <input
                      type="file"
                      name="file"
                      id="image"
                      className="form-control d-none"
                      required
                      onChange={onFileChange}
                      accept="image/png, image/jpeg, image/jpg, image/svg+xml, image/webp, image/x-icon"
                    />
                    <div className="inputfile">
                      <label htmlFor="image">Choose File</label>
                      <span>
                        {imageName
                          ? imageName.trim().split("/").pop()
                          : "No File Choosen"}
                      </span>
                    </div>
                  </div>
                </div>
                {errors?.file && (
                  <div className="text-danger">{errors.file}</div>
                )}
              </div>
              <div className="col-md-12 form-group">
                <label className="profile_details_text">Description</label>
                <textarea
                  name="description"
                  value={data.description}
                  onChange={onChange}
                  maxLength={1000}
                ></textarea>
                {errors?.description && (
                  <div className="text-danger">{errors.description}</div>
                )}
              </div>
              <div className="col-md-6 form-group">
                <FloatingLabelTextInput
                  label={"URL "}
                  onChange={onChange}
                  isMandatory={true}
                  value={data.url}
                  name={"url"}
                  maxLength={500}
                />
                {errors?.url && <div className="text-danger">{errors.url}</div>}
              </div>
              {/* <div className="col-md-6 form-group">
                <FloatingLabelDropDown
                  label={"Is SSO Enabled "}
                  onChange={(e: any) => setIsSSOEnabled(e.target.value)}
                  isMandatory={true}
                  value={isSSOEnabled}
                  name={"isSSOEnabled"}
                  optionsList={ssoOptionsList}
                />
              </div>

              {isSSOEnabled && (
                <div className="col-md-6 form-group">
                  <FloatingLabelTextInput
                    label={"SSO"}
                    onChange={onChange}
                    isMandatory={true}
                    value={data.sso}
                    name={"sso"}
                    maxLength={500}
                  />
                  {errors?.sso && (
                    <div className="text-danger">{errors.sso}</div>
                  )}
                </div>
              )} */}
              <div className="col-md-6 form-group">
                <FloatingLabelDropDown
                  label={"Report Frequency "}
                  onChange={onChange}
                  isMandatory={true}
                  value={data.reportFrequency}
                  name={"reportFrequency"}
                  optionsList={sortAlphabetically(reportFrequencies)}
                />
                {errors?.reportFrequency && (
                  <div className="text-danger">{errors.reportFrequency}</div>
                )}
              </div>
              <div className="col-md-6 form-group">
                <FloatingLabelTextInput
                  label={"College Name "}
                  onChange={onChange}
                  isMandatory={true}
                  value={userDetails?.currentCollege?.name}
                  name={"collegeId"}
                  disable
                />
              </div>
              <div className="col-md-6 form-group">
                <FloatingLabelTextInput
                  label={"Department Name "}
                  onChange={onChange}
                  isMandatory={true}
                  value={userDetails?.currentDepartment?.name}
                  name={"departmentId"}
                  disable
                />
              </div>
              <div
                // className={
                //   isSSOEnabled === true
                //     ? "col-md-12 form-group"
                //     : "col-md-6 form-group"
                // }
                className="col-md-12 form-group"
              >
                <MultipleSelect
                  label={"Page-Block "}
                  onChange={onChange}
                  isMandatory={true}
                  values={data.pageBlockIds as number[]}
                  name={"pageBlockIds"}
                  optionList={sortAlphabetically(pageBlocks)}
                />
                {errors?.pageBlockIds && (
                  <div className="text-danger">{errors.pageBlockIds}</div>
                )}
              </div>
            </div>

            <div className="modal-footer">
              <button
                type="button"
                className="btn btn-primary"
                onClick={onUpdate}
                disabled={isDisabled || !hasChange}
              >
                Update
              </button>
              <button
                type="button"
                className="btn btn-secondary"
                data-bs-dismiss="modal"
                onClick={() => setShowEdit(false)}
                disabled={isDisabled}
              >
                Close
              </button>
            </div>
          </div>
          <SweetAlert
            show={showAlert}
            success={false}
            type={alertType}
            title={alertTitle}
            onConfirm={() => onConfirm()}
            showCancel={showAlertCancel}
            onCancel={() => {
              setShowAlert(false);
              setShowAlertCancel(false);
            }}
            cancelBtnText={"No"}
            confirmBtnText={showAlertCancel ? "Yes" : "Ok"}
          ></SweetAlert>
          {loading && <LoaderSpinner />}
        </>
      )}
    </>
  );
};

const mapDispatchToProps = {
  updateReport: (data: any, id: number, callBack: Function) =>
    updateReport(data, id, callBack),
  getReportFrequencies: () => getreportFrequencies(),
  getReportNamesWithDescription: () => getReportNamesWithDescription(),
  getUserPageBlocks: (userType: number, masterType: string) =>
    getUserPageBlocks(userType, masterType),
  getUserTypes: () => getUserTypes(),
};

function mapStateToProps(state: any) {
  return {
    pageBlocks: state.common.pageBlocks,
    reportFrequencies: state.common.reportFrequencies,
    reportNamesWithDescription: state.common.reportNamesWithDescription,
    userTypes: state.common.userTypes,
    userDetails: state.userDetails.userProfileData,
  };
}

export default connect(mapStateToProps, mapDispatchToProps)(EditReport);
